import {
  lightModeMarkerColors,
  darkModeMarkerColors,
} from 'features/spectrumChart/utils/colors';
import {
  SnapshotMarker,
  SnapshotMonitor,
  SnapshotSpectralMask,
  SnapshotThreshold,
} from 'types';

const INVALID_POINT = -10000;

export const drawGrid = (ctx: CanvasRenderingContext2D, color: string) => {
  let numberOfXGridLines = 10;
  const numberOfYGridlines = 10;
  numberOfXGridLines = 2 * Math.round(numberOfXGridLines / 2);

  for (let i = 0; i < numberOfXGridLines; i++) {
    const x = Math.floor((i * ctx.canvas.width) / numberOfXGridLines);

    ctx.fillStyle = color;
    ctx.fillRect(x, 0, 1, ctx.canvas.height);
  }
  const x = Math.floor((9.99 * ctx.canvas.width) / numberOfXGridLines);
  ctx.fillRect(x, 0, 1, ctx.canvas.height);

  for (let i = 0; i < numberOfYGridlines; i++) {
    const y = Math.round((i * ctx.canvas.height) / numberOfYGridlines);

    ctx.fillStyle = color;
    ctx.fillRect(0, y, ctx.canvas.width, 1);
  }

  const y = Math.round((9.95 * ctx.canvas.height) / numberOfYGridlines);
  ctx.fillRect(0, y, ctx.canvas.width, 1);
};

export const outlinedText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  fillStyle?: string | undefined,
  w?: number | undefined
) => {
  ctx.strokeStyle = 'black';
  if (!fillStyle) {
    ctx.fillStyle = 'white';
  } else {
    ctx.fillStyle = fillStyle;
  }
  ctx.lineWidth = 3;
  w = w || 9e9;

  ctx.miterLimit = 2;
  ctx.strokeText(text, x, y, w);
  ctx.fillText(text, x, y, w);
  ctx.miterLimit = 10.0;
};

export const drawTitle = (ctx: CanvasRenderingContext2D, title: string) => {
  const x = Math.floor((5 * ctx.canvas.width) / 10);
  const y = Math.round(ctx.canvas.height / 10) / 2;
  ctx.textAlign = 'center';
  ctx.font = '12px sans-serif';
  ctx.fillStyle = 'white';
  ctx.textBaseline = 'top';
  outlinedText(ctx, title, x, y);
};

export const drawXLabels = (
  ctx: CanvasRenderingContext2D,
  xLabels: string[]
) => {
  ctx.font = '12px sans-serif';
  ctx.fillStyle = 'white';
  ctx.textBaseline = 'bottom';

  let x = Math.floor((0.6 * ctx.canvas.width) / 10);
  const y = Math.round((11 * ctx.canvas.height) / 11) - 5;

  if (ctx.canvas.width >= 700) {
    outlinedText(ctx, xLabels[0] || '', x, y);
    x = Math.floor((9.5 * ctx.canvas.width) / 10);
    outlinedText(ctx, xLabels[2] || '', x, y);
  }

  x = Math.floor((5 * ctx.canvas.width) / 10);
  outlinedText(ctx, xLabels[1] || '', x, y);
};

export const drawYLabels = (
  ctx: CanvasRenderingContext2D,
  ylabels: string[]
) => {
  const labelPad = 5;
  const fontSize = 14;
  let line_height = 1.2 * fontSize + labelPad;

  ctx.font = 'px sans-serif';
  ctx.fillStyle = 'white';
  ctx.textAlign = 'left';

  let x = labelPad;

  if (ylabels.length * line_height > ctx.canvas.height) {
    if (ylabels.length > 0) {
      ctx.textBaseline = 'top';
      let y = labelPad;
      outlinedText(ctx, ylabels[0] || '', x, y);
    }
    if (ylabels.length > 1) {
      ctx.textBaseline = 'bottom';
      let y = ctx.canvas.height - labelPad;
      let txt = ylabels[ylabels.length - 1] || '';
      outlinedText(ctx, txt, x, y);
    }
  } else {
    ctx.textBaseline = 'top';
    for (let i = 0; i < ylabels.length; i++) {
      let y = Math.round((i * ctx.canvas.height) / ylabels.length);
      outlinedText(ctx, ylabels[i] || '', x, y + labelPad);
    }
  }
};

const amplitudeToPixels = (amplitude: number, height: number) => {
  let y = Math.max(0, Math.min(1, amplitude));
  return Math.max(1, Math.min(height - 2, Math.round(height * (1 - y))));
};

export const pixelToAmplitude = (
  pixel: number,
  height: number,
  reference: number,
  scale: number
) => {
  return denormalize(1 - pixel / height, reference, scale);
};

const indexToPixel = (
  ctx: CanvasRenderingContext2D,
  index: number,
  traceLength: number
) => (ctx.canvas.width / traceLength) * index;

export const pixelToIndex = (
  ctx: CanvasRenderingContext2D,
  pixel: number,
  traceLength: number
) => Math.floor(pixel / (ctx.canvas.width / traceLength));

export const normalizeTrace = (
  traceData: number[],
  reference: number,
  scale: number
) => {
  if (!traceData || traceData.length === 0) return [];
  return traceData.map((point) => normalizePoint(point, reference, scale));
};

const normalizePoint = (point: number, reference: number, scale: number) => {
  let adjustedScale = scale * 10;
  return (point - reference + adjustedScale) / adjustedScale;
};

const denormalize = (point: number, reference: number, scale: number) => {
  let adjustedScale = scale * 10;
  return point * adjustedScale + reference - adjustedScale;
};

export const clearCanvas = (ctx: CanvasRenderingContext2D) => {
  if (!ctx) return;
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
};

export const drawTrace = (
  ctx: CanvasRenderingContext2D,
  data: number[],
  color: string,
  reference: number,
  scale: number
): number[] => {
  const normalizedData = normalizeTrace(data, reference, scale);
  let width = ctx.canvas.width;
  let height = ctx.canvas.height;
  let round = Math.round;
  let len = data.length;

  let points = normalizedData.map(function (ampl, i) {
    let ret: {
      x: number;
      y: number;
      alpha: number;
      color: string;
      valid?: boolean;
    } = {
      x: 0.5 + round((width * i) / (len - 1)),
      y: 0.5 + round(amplitudeToPixels(ampl, height)),
      alpha: 1.0,
      color: color,
    };
    ret.valid = !isNaN(ret.x) && !isNaN(ret.y) && data[i] !== INVALID_POINT;
    return ret;
  });

  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'bevel';
  ctx.beginPath();

  let pixelTrace: number[] = [];

  for (let i = 0; i < len; i++) {
    let prev = points[i - 1] || {
      valid: false,
    };
    let point = points[i];

    if (point.color !== prev.color || point.alpha !== prev.alpha) {
      ctx.stroke();
      ctx.globalAlpha = point.alpha;
      ctx.strokeStyle = point.color;
      ctx.beginPath();

      if (prev.valid) {
        ctx.moveTo(prev.x, prev.y);
      }
    }

    if (point.valid) {
      if (prev.valid) {
        ctx.lineTo(point.x, point.y);
        pixelTrace.push(point.y);
      } else ctx.moveTo(point.x, point.y);
    }
  }

  ctx.stroke();
  return pixelTrace;
};

export const drawTimestamp = (
  ctx: CanvasRenderingContext2D,
  timestamp: string,
  timeWarningColor: string
) => {
  const x = (ctx.canvas.width / 10) * 1.25;
  const y = ctx.canvas.height * 0.98;
  const calculatedFontSize = ctx.canvas.width / 50;
  let fontSize = calculatedFontSize >= 10 ? calculatedFontSize : 0;

  if (fontSize > 12) fontSize = 12;

  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'left';
  ctx.font = `${fontSize}px sans-serif`;
  outlinedText(ctx, timestamp, x, y, timeWarningColor);
};

export function handleResize(ctx: CanvasRenderingContext2D) {
  const { width, height } = ctx.canvas.getBoundingClientRect();
  const floorHeight = Math.floor(height);
  const floorWidth = Math.floor(width);

  ctx.canvas.width = floorWidth;
  ctx.canvas.height = floorHeight;
}

export const drawMonitor = (
  ctx: CanvasRenderingContext2D,
  name: string,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  pixelTrace: number[],
  warningMode: boolean | 0 | 1 | undefined,
  hideDetectorNames?: boolean | undefined
) => {
  const width = endX - startX;
  const height = endY - startY;

  const startIndex = pixelToIndex(ctx, startX, pixelTrace.length);
  const endIndex = pixelToIndex(ctx, endX, pixelTrace.length);

  const splitPixelTrace = pixelTrace.slice(startIndex, endIndex);
  const outOfBoundsValues = splitPixelTrace.filter(
    (pixel) => pixel >= endY || pixel <= startY
  );

  const alarming = outOfBoundsValues.length > 0 ? true : false;

  let monitorFillColor = 'rgba(0, 255, 0, 0.2)';
  let monitorStrokeColor = 'rgba(0, 255, 0, 1)';

  if (alarming && warningMode) {
    monitorFillColor = 'rgba(245, 131, 39, 0.2)';
    monitorStrokeColor = 'rgba(245, 131, 39, 1)';
  } else if (alarming) {
    monitorFillColor = 'rgba(255, 0, 0, 0.2)';
    monitorStrokeColor = 'rgba(255, 0, 0, 1)';
  }

  ctx.strokeStyle = monitorStrokeColor;
  ctx.lineWidth = 2;
  ctx.fillStyle = monitorFillColor;
  ctx.fillRect(startX, startY, width, height);
  ctx.strokeRect(startX, startY, width, height);

  if (!hideDetectorNames) {
    outlinedText(ctx, name, startX, startY);
  }
};

export const drawMonitors = (
  ctx: CanvasRenderingContext2D,
  monitors: SnapshotMonitor[] | undefined,
  pixelTrace: number[],
  reference: number,
  scale: number,
  hideDetectorNames?: boolean | undefined
) => {
  if (!monitors) return;

  for (let i = 0; i < monitors.length; i++) {
    const monitor = monitors[i];
    const monitorBegin = indexToPixel(ctx, monitor.begin, pixelTrace.length);
    const monitorEnd = indexToPixel(ctx, monitor.end, pixelTrace.length);
    const monitorUpper = amplitudeToPixels(
      normalizePoint(monitor.upperbound / 1000, reference, scale),
      ctx.canvas.height
    );
    const monitorLower = amplitudeToPixels(
      normalizePoint(monitor.lowerbound / 1000, reference, scale),
      ctx.canvas.height
    );
    drawMonitor(
      ctx,
      monitor.name,
      monitorBegin,
      monitorUpper,
      monitorEnd,
      monitorLower,
      pixelTrace,
      monitor.warning,
      hideDetectorNames
    );
  }
};

export const drawTemporaryMarker = (
  ctx: CanvasRenderingContext2D,
  x: number,
  traceData: number[] | undefined,
  snapshotFrequencies: string[],
  centerUnit: string,
  reference: number,
  scale: number
) => {
  if (!traceData) return;
  const markerColor = 'rgba(255, 255, 255, 0.5)';

  const markerXPos = pixelToIndex(ctx, x, traceData.length);
  const frequency = snapshotFrequencies[markerXPos] || '';
  const amplitude = traceData[markerXPos];
  const y = amplitudeToPixels(
    normalizePoint(amplitude, reference, scale),
    ctx.canvas.height
  );

  ctx.beginPath();
  ctx.fillStyle = markerColor;
  ctx.strokeStyle = markerColor;
  ctx.lineWidth = 3;
  ctx.moveTo(x - 8, y - 15);
  ctx.lineTo(x, y);
  ctx.moveTo(x + 8, y - 15);
  ctx.lineTo(x, y);
  ctx.lineTo(x - 8, y - 15);
  ctx.fill();

  //Draw marker values in top right of chart
  const calculatedFontSize = ctx.canvas.width / 50;
  let markerValueFontSize = calculatedFontSize >= 10 ? calculatedFontSize : 0;
  if (markerValueFontSize > 12) markerValueFontSize = 12;
  ctx.font = `${markerValueFontSize}px sans-serif`;
  ctx.textAlign = 'left';
  outlinedText(
    ctx,
    `X: ${frequency} ${centerUnit} Y: ${amplitude} dBm`,
    ctx.canvas.width * 0.05,
    (ctx.canvas.height * 0.05) / 1.5
  );
};

export const drawMarker = (
  ctx: CanvasRenderingContext2D,
  x: number,
  traceData: number[] | undefined,
  markerIndex: number,
  reference: number,
  scale: number,
  inLightMode: boolean
) => {
  if (!traceData) return;

  const markerColor = inLightMode
    ? lightModeMarkerColors[markerIndex]
    : darkModeMarkerColors[markerIndex];

  const markerXPos = pixelToIndex(ctx, x, traceData.length);
  const amplitude = traceData[markerXPos];
  const y = amplitudeToPixels(
    normalizePoint(amplitude, reference, scale),
    ctx.canvas.height
  );

  ctx.beginPath();
  ctx.fillStyle = markerColor;
  ctx.strokeStyle = markerColor;
  ctx.lineWidth = 3;
  ctx.moveTo(x - 8, y - 15);
  ctx.lineTo(x, y);
  ctx.moveTo(x + 8, y - 15);
  ctx.lineTo(x, y);
  ctx.lineTo(x - 8, y - 15);
  ctx.fill();

  ctx.textAlign = 'center';
  ctx.font = '12px sans-serif';
  ctx.fillStyle = 'white';
  outlinedText(ctx, `M${markerIndex + 1}`, x, y - ctx.canvas.width * 0.03);

  //Draw marker values in top right of chart
  const calculatedFontSize = ctx.canvas.width / 50;
  let markerValueFontSize = calculatedFontSize >= 10 ? calculatedFontSize : 0;
  if (markerValueFontSize > 12) markerValueFontSize = 12;
  ctx.font = `${markerValueFontSize}px sans-serif`;
  ctx.textAlign = 'left';
  if (!amplitude) return;
  outlinedText(
    ctx,
    `M${markerIndex + 1}: ${amplitude} dBm`,
    ctx.canvas.width * 0.85,
    (ctx.canvas.height * 0.05 * (markerIndex + 1)) / 1.5
  );
};

export const drawTrendlineMarker = (
  ctx: CanvasRenderingContext2D,
  x: number,
  traceData: number[] | undefined,
  markerIndex: number,
  reference: number,
  scale: number,
  inLightMode: boolean
) => {
  if (!traceData) return;

  const markerColor = inLightMode
    ? lightModeMarkerColors[markerIndex - 100]
    : darkModeMarkerColors[markerIndex - 100];

  const markerXPos = pixelToIndex(ctx, x, traceData.length);
  const amplitude = traceData[markerXPos];
  const y = amplitudeToPixels(
    normalizePoint(amplitude, reference, scale),
    ctx.canvas.height
  );

  ctx.beginPath();
  ctx.fillStyle = markerColor;
  ctx.strokeStyle = markerColor;
  ctx.lineWidth = 3;
  ctx.moveTo(x - 8, y - 15);
  ctx.lineTo(x, y);
  ctx.moveTo(x + 8, y - 15);
  ctx.lineTo(x, y);
  ctx.lineTo(x - 8, y - 15);
  ctx.fill();

  ctx.textAlign = 'center';
  ctx.font = '12px sans-serif';
  ctx.fillStyle = 'white';
  outlinedText(
    ctx,
    `T${markerIndex + 1 - 100}`,
    x,
    y - ctx.canvas.width * 0.03
  );
};

export const drawCrosspolMarker = (
  ctx: CanvasRenderingContext2D,
  traceData: number[],
  reference: number,
  scale: number,
  markerIndex: number,
  peakSearch: boolean | undefined
) => {
  if (!peakSearch) {
    const centerIndex = (traceData.length - 1) / 2;
    const xPosition = indexToPixel(ctx, centerIndex, traceData.length);
    drawMarker(ctx, xPosition, traceData, markerIndex, reference, scale, false);
  } else {
    const tracePeak = Math.max(...traceData);
    const index = traceData.indexOf(tracePeak);
    const xPosition = indexToPixel(ctx, index, traceData.length);
    drawMarker(ctx, xPosition, traceData, markerIndex, reference, scale, false);
  }
};

export const drawMarkers = (
  ctx: CanvasRenderingContext2D,
  markers: SnapshotMarker[] | undefined,
  traceData: number[],
  reference: number,
  scale: number,
  inLightMode: boolean
) => {
  if (!markers) return;

  const filteredMarkers = markers.filter((marker) => marker.index < 4);

  for (let i = 0; i < filteredMarkers.length; i++) {
    const marker = filteredMarkers[i];
    const xPosition = indexToPixel(ctx, marker.x_pos, traceData.length);
    drawMarker(
      ctx,
      xPosition,
      traceData,
      marker.index,
      reference,
      scale,
      inLightMode
    );
  }
};

export const drawTrendlineMarkers = (
  ctx: CanvasRenderingContext2D,
  markers: SnapshotMarker[] | undefined,
  traceData: number[],
  reference: number,
  scale: number,
  inLightMode: boolean
) => {
  if (!markers) return;

  const filteredMarkers = markers.filter((marker) => marker.index >= 100);

  for (let i = 0; i < filteredMarkers.length; i++) {
    const marker = filteredMarkers[i];
    const xPosition = indexToPixel(ctx, marker.x_pos, traceData.length);
    drawTrendlineMarker(
      ctx,
      xPosition,
      traceData,
      marker.index,
      reference,
      scale,
      inLightMode
    );
  }
};

export const drawThreshold = (
  ctx: CanvasRenderingContext2D,
  name: string,
  startX: number,
  startY: number,
  endX: number,
  pixelTrace: number[],
  solidLine?: boolean,
  hideDetectorNames?: boolean | undefined
) => {
  if (startX > endX) {
    //Handles Right to Left functionality when drawing
    const temp = startX;
    startX = endX;
    endX = temp;
  }

  const startIndex = pixelToIndex(ctx, startX, pixelTrace.length);
  const endIndex = pixelToIndex(ctx, endX, pixelTrace.length);
  const splitPixelTrace = pixelTrace.slice(startIndex, endIndex);
  const outOfBoundsValues = splitPixelTrace.filter((pixel) => pixel <= startY);
  const alarming = outOfBoundsValues.length > 0 ? true : false;

  if (solidLine) {
    ctx.strokeStyle = alarming ? 'red' : 'green';
  } else {
    ctx.strokeStyle = 'gray';
  }
  ctx.lineWidth = 2;

  if (!solidLine) {
    ctx.setLineDash([5, 5]);
  }

  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, startY);
  ctx.stroke();
  ctx.setLineDash([]);
  if (!hideDetectorNames) {
    outlinedText(ctx, name, startX, startY - 20);
  }
};

export const drawThresholds = (
  ctx: CanvasRenderingContext2D,
  thresholds: SnapshotThreshold[] | undefined,
  traceLength: number,
  pixelTrace: number[],
  reference: number,
  scale: number,
  hideDetectorNames?: boolean | undefined
) => {
  if (!traceLength || !thresholds || thresholds.length === 0) return;

  for (let i = 0; i < thresholds.length; i++) {
    const threshold = thresholds[i];
    const isSolidLine = !threshold?.warning;
    const begin = indexToPixel(ctx, threshold.begin, traceLength);
    const end = indexToPixel(ctx, threshold.end, traceLength);
    const yValue = amplitudeToPixels(
      normalizePoint(threshold.bound / 1000, reference, scale),
      ctx.canvas.height
    );

    drawThreshold(
      ctx,
      threshold.name,
      begin,
      yValue,
      end,
      pixelTrace,
      isSolidLine,
      hideDetectorNames
    );
  }
};

export const drawMask = (
  ctx: CanvasRenderingContext2D,
  name: string,
  baseline: number[],
  begin: number,
  end: number,
  upperbound: number,
  lowerbound: number,
  inDrawingMode = false,
  traceData: number[],
  reference: number,
  scale: number,
  hideDetectorNames?: boolean | undefined
) => {
  let beginIndex = begin;
  let endIndex = end;

  if (inDrawingMode) {
    beginIndex = pixelToIndex(ctx, begin, baseline.length);
    endIndex = pixelToIndex(ctx, end, baseline.length);
    outlinedText(
      ctx,
      `+${upperbound}`,
      begin,
      amplitudeToPixels(
        normalizePoint(baseline[beginIndex] + upperbound + 5, reference, scale),
        ctx.canvas.height
      )
    );
    outlinedText(
      ctx,
      `-${lowerbound}`,
      begin,
      amplitudeToPixels(
        normalizePoint(baseline[beginIndex] - lowerbound - 5, reference, scale),
        ctx.canvas.height
      )
    );
  }

  const upperMask = baseline.map((point, i) =>
    i >= beginIndex && i <= endIndex ? point + upperbound : INVALID_POINT
  );
  const lowerMask = baseline.map((point, i) =>
    i >= beginIndex && i <= endIndex ? point - lowerbound : INVALID_POINT
  );

  const middleXPixel = indexToPixel(
    ctx,
    Math.floor((begin + end) / 2) - 5,
    traceData.length
  );

  let upperMaskAlarming = false;
  let lowerMaskAlarming = false;

  for (let i = beginIndex; i <= endIndex; i++) {
    if (upperMaskAlarming && lowerMaskAlarming) break;

    if (!upperMaskAlarming && traceData[i] >= upperMask[i]) {
      upperMaskAlarming = true;
    }
    if (!lowerMaskAlarming && traceData[i] <= lowerMask[i])
      lowerMaskAlarming = true;
  }

  let upperMaskColor = 'green';
  let lowerMaskColor = 'green';

  if (upperMaskAlarming) upperMaskColor = 'red';
  if (lowerMaskAlarming) lowerMaskColor = 'red';

  const pixelUpperMask = drawTrace(
    ctx,
    upperMask,
    upperMaskColor,
    reference,
    scale
  );
  const middleYPixel = Math.ceil((pixelUpperMask.length - 1) / 2);
  drawTrace(ctx, lowerMask, lowerMaskColor, reference, scale);

  if (!hideDetectorNames) {
    outlinedText(ctx, name, middleXPixel, pixelUpperMask[middleYPixel] - 20);
  }
};

export const drawMasks = (
  ctx: CanvasRenderingContext2D,
  masks: SnapshotSpectralMask[] | undefined,
  baseline: number[],
  traceData: number[],
  reference: number,
  scale: number,
  hideDetectorNames?: boolean | undefined
) => {
  if (!masks || masks.length === 0 || !baseline || baseline.length === 0)
    return;

  for (let i = 0; i < masks.length; i++) {
    const mask = masks[i];
    drawMask(
      ctx,
      mask.name,
      baseline,
      mask.begin,
      mask.end,
      mask.upperbound / 1000,
      mask.lowerbound / 1000,
      false,
      traceData,
      reference,
      scale,
      hideDetectorNames
    );
  }
};
